/** @format */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	signUp,
	confirmAndVerifyAccount,
	resendVerificationCodeAction,
	signIn,
	logOut,
	update,
	passWordReset,
	confirmPassWordReset,
	getUser,
	fetchSearchUserToSendAddRequest,
	fetchAllCirclesOfMember,
	getMultipleUsersById,
	fetchUserRating,
	fetchAllJoinRequestsOfUser,
	getUserStats,
	getAllUsersAsAdmin,
	banUser,
	unbanUser,
	deleteUser,
	restoreUser,
	getUserByIdAsAdmin,
	updateUserByIdAsAdmin,
	deleteUserByIdAsAdmin,
} from "../actions/userActions";
import {
	getAutoDraftUsersAsAdmin,
	getAutoDraftUserByIdAsAdmin,
	getAutoDraftSummaryAsAdmin,
	cancelAutoDraftAsAdmin,
	getAutoDraftResultsAsAdmin,
	getAutoDraftResultsByDateAsAdmin,
	getAutoDraftFailedAsAdmin,
	getAutoDraftRetriesAsAdmin,
	getAutoDraftAttemptByIdAsAdmin,
	AutoDraftUserInfo,
	AutoDraftSummaryData,
	AutoDraftResultForDateResponse,
	AutoDraftFailedAttemptsResponse,
	AutoDraftRetryAttemptsResponse,
	AutoDraftAttemptDetailed,
} from "../actions/autoDraftAction";
import {
	AllUsersResponse,
	Circle,
	IUser,
	IUserAccessPayload,
	IUserFinancialSummary,
	IUserPayload,
	MemberCircles,
	UserJoinRequests,
	UserRating,
} from "../../utility/types/userType";
import {
	IUserVerification,
	IVerifyAndConfirmAccountPayload,
} from "../../components/verifyAndConfirmAccount/types";
import { checkAuthenticationOnPageLoad } from "../../auth/tokenAuth";
import {
	acceptAddRequest,
	acceptJoinRequest,
	fetchAddRequest,
	getAccountVerificationStatus,
} from "../actions/circleAction";
import {
	// IAccountVerification,
	IAccountVerificationPayload,
} from "../../utility/types/circleType";

export interface UserState {
	userData: IUserPayload | null;
	userAccess: IUserAccessPayload | null;
	userVerifyData: IVerifyAndConfirmAccountPayload | null;
	// userVerification: IUserVerification | null;
	isAuthenticated: boolean; //tracks authentication status
	userCircles: MemberCircles | null;
	error: string | null;
	isLoading: boolean;
	searchUserData: IUser | null;
	userMap: Record<string, IUserPayload>;
	userRating: UserRating | null;
	userJoinRequests: UserJoinRequests | null;
	userFinancialSummary: IUserFinancialSummary | null;
	allUsers: AllUsersResponse | null;
	// Auto-Draft Admin State
	autoDraftUsers: AutoDraftUserInfo[] | null;
	autoDraftUserDetail: AutoDraftUserInfo | null;
	autoDraftSummary: AutoDraftSummaryData | null;
	autoDraftLoading: boolean;
	autoDraftError: string | null;
	// Auto-Draft Results State
	autoDraftResults: AutoDraftResultForDateResponse | null;
	autoDraftFailed: AutoDraftFailedAttemptsResponse | null;
	autoDraftRetries: AutoDraftRetryAttemptsResponse | null;
	autoDraftAttemptDetail: AutoDraftAttemptDetailed | null;
	autoDraftResultsLoading: boolean;
	autoDraftResultsError: string | null;
}

const initialState: UserState = {
	userData: null,
	userAccess: null,
	userVerifyData: null,
	isAuthenticated: false,
	userCircles: null,
	error: null,
	isLoading: false,
	searchUserData: null,
	userMap: {},
	userRating: null,
	userJoinRequests: null,
	userFinancialSummary: null,
	allUsers: null,
	// Auto-Draft Admin State
	autoDraftUsers: null,
	autoDraftUserDetail: null,
	autoDraftSummary: null,
	autoDraftLoading: false,
	autoDraftError: null,
	// Auto-Draft Results State
	autoDraftResults: null,
	autoDraftFailed: null,
	autoDraftRetries: null,
	autoDraftAttemptDetail: null,
	autoDraftResultsLoading: false,
	autoDraftResultsError: null,
	// userVerification: null,
};

export interface AccountVerificationState {
	accountVerificationPayload: IAccountVerificationPayload | null;
	accountVerificationData: IUserVerification | null;
	isLoading: boolean;
	error: string;
}

const initialAccountVerificationState: AccountVerificationState = {
	accountVerificationData: null,
	accountVerificationPayload: null,
	isLoading: false,
	error: "",
};

export const accountVerificationSlice = createSlice({
	name: "accountVerification",
	initialState: initialAccountVerificationState,
	reducers: {
		setAccountVerificationData: (
			state,
			action: PayloadAction<IAccountVerificationPayload>,
		) => {
			state.accountVerificationPayload = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(
			getAccountVerificationStatus.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			getAccountVerificationStatus.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.accountVerificationData = action.payload;
			},
		);

		builder.addCase(
			getAccountVerificationStatus.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send  getVerificationStatus request. Unknown error occurred";
			},
		);
	},
});

export const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		setAuthenticationState: (state, action: PayloadAction<boolean>) => {
			state.isAuthenticated = action.payload;
		},

		setUserAccessState: (state, action: PayloadAction<any>) => {
			state.userAccess = action.payload;
		},

		updateUserProfileImage: (state, action: PayloadAction<any>) => {
			if (state.userData) {
				state.userData.profileImage = action.payload;
			}
		},
	},

	extraReducers: (builder) => {
		builder.addCase(signIn.pending, (state, action) => {
			state.isLoading = true;
		});

		builder.addCase(signIn.fulfilled, (state, action) => {
			if (action.payload) {
				// state.userAccess = {
				//     accessToken: action.payload.accessToken,
				//     idToken: action.payload.idToken
				// }
				const { user, tokens } = action.payload;
				state.userData = user;
				state.isAuthenticated = true;
				// Store all tokens in localStorage for mobile browser support
				// Mobile Safari (iOS) blocks third-party cookies, so we send tokens
				// via Authorization header instead. This is set up in axiosInterceptor.ts
				localStorage.setItem("idToken", tokens.idToken);
				if (tokens.accessToken) {
					localStorage.setItem("accessToken", tokens.accessToken);
				}
				if (tokens.refreshToken) {
					localStorage.setItem("refreshToken", tokens.refreshToken);
				}
				state.error = null;
			} else {
				state.isAuthenticated = false;
				state.error =
					"Sign In Failed OR Additional authentication steps required";
			}
			state.isLoading = false;
		});

		builder.addCase(signIn.rejected, (state, action) => {
			state.error = action.error.message || "Failed to sign in.";
			state.isAuthenticated = false;
			state.isLoading = false;
		});

		builder.addCase(getUser.pending, (state, action: PayloadAction<any>) => {
			state.isLoading = true;
		});

		builder.addCase(getUser.fulfilled, (state, action: PayloadAction<any>) => {
			const currentPendingAddRequests = state.userData?.pendingAddRequests;
			state.userData = {
				...state.userData,
				...action.payload,
				pendingAddRequests:
					currentPendingAddRequests || action.payload?.pendingAddRequests,
			};
			state.isLoading = false;
			state.error = null;
		});

		builder.addCase(getUser.rejected, (state, action: PayloadAction<any>) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to fetch user.";
		});

		builder.addCase(getMultipleUsersById.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			getMultipleUsersById.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = null;

				// If, for some reason, userMap is undefined,
				// forcibly ensure it's an object:
				if (!state.userMap) {
					state.userMap = {};
				}

				const userArray = action.payload;
				userArray.forEach((user: any) => {
					if (user._id) {
						state.userMap[user._id] = user;
					}
				});
			},
		);

		builder.addCase(
			getMultipleUsersById.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload || "Failed to fetch users.";
			},
		);

		builder.addCase(
			fetchSearchUserToSendAddRequest.pending,
			(state, action) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			fetchSearchUserToSendAddRequest.fulfilled,
			(state, action) => {
				state.isLoading = false;
				state.searchUserData = {
					...action.payload,
				};
				state.error = null;
			},
		);

		builder.addCase(
			fetchSearchUserToSendAddRequest.rejected,
			(state, action) => {
				state.isLoading = false;
				if (typeof action.payload === "string") {
					state.error = action.payload || "Failed to search user.";
				} else {
					state.error = "An unexpected error occurred."; // Fallback error message
				}
			},
		);

		builder.addCase(signUp.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(signUp.fulfilled, (state, action: PayloadAction<any>) => {
			state.isLoading = false;
			state.userData = action.payload;
		});

		builder.addCase(signUp.rejected, (state, action: PayloadAction<any>) => {
			state.isLoading = false;
			state.error = action.payload;
		});

		builder.addCase(logOut.fulfilled, (state, action: PayloadAction<any>) => {
			state.userData = null;
			state.isAuthenticated = false;
			state.error = null;
			state.isLoading = false;
			// Clear all tokens from localStorage (used for mobile browser auth)
			localStorage.removeItem("idToken");
			localStorage.removeItem("accessToken");
			localStorage.removeItem("refreshToken");
		});

		builder.addCase(logOut.rejected, (state, action: PayloadAction<any>) => {
			state.isLoading = false;
			state.error = action.payload;
		});

		builder.addCase(
			confirmAndVerifyAccount.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			confirmAndVerifyAccount.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.userVerifyData = action.payload;
			},
		);

		builder.addCase(
			confirmAndVerifyAccount.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		// Resend verification code cases
		builder.addCase(resendVerificationCodeAction.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			resendVerificationCodeAction.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				// You can add any specific state updates here if needed
			},
		);

		builder.addCase(
			resendVerificationCodeAction.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		builder.addCase(
			checkAuthenticationOnPageLoad.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isAuthenticated = action.payload.userIsAuthenticated;
				// Preserve pendingAddRequests when updating userData from auth check
				const currentPendingAddRequests = state.userData?.pendingAddRequests;
				state.userData = {
					...action.payload.user,
					pendingAddRequests:
						currentPendingAddRequests ||
						action.payload.user?.pendingAddRequests,
				};
			},
		);

		builder.addCase(
			checkAuthenticationOnPageLoad.rejected,
			(state, action: PayloadAction<any>) => {
				state.isAuthenticated = false;
				state.error =
					action.payload || "Failed to verify authentication on page reload";
			},
		);

		builder.addCase(update.pending, (state, action: PayloadAction<any>) => {
			state.isLoading = true;
		});

		builder.addCase(update.fulfilled, (state, action: PayloadAction<any>) => {
			state.isLoading = false;
			// Preserve pendingAddRequests when updating userData
			const currentPendingAddRequests = state.userData?.pendingAddRequests;
			state.userData = {
				...action.payload,
				pendingAddRequests:
					currentPendingAddRequests || action.payload?.pendingAddRequests,
			};
		});

		builder.addCase(update.rejected, (state, action: PayloadAction<any>) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to perform update.";
		});

		builder.addCase(passWordReset.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			passWordReset.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				if (action.payload) {
					// Preserve pendingAddRequests when updating userData
					const currentPendingAddRequests = state.userData?.pendingAddRequests;
					state.userData = {
						...action.payload,
						pendingAddRequests:
							currentPendingAddRequests || action.payload?.pendingAddRequests,
					};
				}
			},
		);

		builder.addCase(
			passWordReset.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		builder.addCase(confirmPassWordReset.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			confirmPassWordReset.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				if (action.payload) {
					// Preserve pendingAddRequests when updating userData
					const currentPendingAddRequests = state.userData?.pendingAddRequests;
					state.userData = {
						...action.payload,
						pendingAddRequests:
							currentPendingAddRequests || action.payload?.pendingAddRequests,
					};
				}
			},
		);

		builder.addCase(
			confirmPassWordReset.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		builder.addCase(
			fetchAddRequest.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder.addCase(
			fetchAddRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				if (state.userData) {
					// Assuming 'pendingAddRequests' is part of 'userData' and you want to update it
					state.userData.pendingAddRequests = action.payload;
				}
			},
		);

		builder.addCase(
			fetchAddRequest.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		builder.addCase(
			acceptAddRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				if (action.payload.user) {
					// Preserve pendingAddRequests when updating userData
					const currentPendingAddRequests = state.userData?.pendingAddRequests;
					state.userData = {
						...state.userData,
						...action.payload.user,
						pendingAddRequests:
							currentPendingAddRequests ||
							action.payload.user.pendingAddRequests,
					};
					if (
						!state.userData?.unions?.some(
							(u) => u._id === action.payload.union._id,
						)
					) {
						// add union to the user union/circle list if it's not already there.
						state.userData!.unions = [
							...(state.userData?.unions || []),
							action.payload.union,
						];
					}
				}
			},
		);

		builder.addCase(
			acceptJoinRequest.fulfilled,
			(state, action: PayloadAction<any>) => {
				const { circle } = action.payload;

				if (state.userData) {
					const currentUnions = state.userData.unions || [];
					if (!currentUnions.some((union: any) => union._id === circle._id)) {
						state.userData.unions = [...currentUnions, circle];
					}
				}
			},
		);

		builder.addCase(fetchAllCirclesOfMember.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			fetchAllCirclesOfMember.fulfilled,
			(state, action: PayloadAction<Circle[]>) => {
				state.userCircles = {
					message: "successfully fetched all the circles member belongs to.",
					circles: action.payload,
				};
				state.isLoading = false;
				state.error = null;
			},
		);

		builder.addCase(
			fetchAllCirclesOfMember.rejected,
			(state, action: PayloadAction<any>) => {
				state.error = action.payload;
				state.isLoading = false;
			},
		);

		builder.addCase(fetchUserRating.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			fetchUserRating.fulfilled,
			(state, action: PayloadAction<UserRating>) => {
				state.isLoading = false;
				state.userRating = action.payload;
				state.error = null;
			},
		);

		builder.addCase(
			fetchUserRating.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		builder.addCase(fetchAllJoinRequestsOfUser.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(
			fetchAllJoinRequestsOfUser.fulfilled,
			(state, action: PayloadAction<UserJoinRequests>) => {
				state.isLoading = false;
				state.userJoinRequests = action.payload;
				state.error = null;
			},
		);

		builder.addCase(
			fetchAllJoinRequestsOfUser.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload;
			},
		);

		builder.addCase(getUserStats.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(getUserStats.fulfilled, (state, action: any) => {
			state.isLoading = false;
			state.userFinancialSummary = action.payload.data;
		});

		builder.addCase(getUserStats.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload;
		});

		// Get all users as admin
		builder.addCase(getAllUsersAsAdmin.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			getAllUsersAsAdmin.fulfilled,
			(state, action: PayloadAction<AllUsersResponse>) => {
				state.isLoading = false;
				state.allUsers = action.payload;
				state.error = null;
			},
		);

		builder.addCase(getAllUsersAsAdmin.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to fetch users";
		});

		// Ban user
		builder.addCase(banUser.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(banUser.fulfilled, (state) => {
			state.isLoading = false;
			state.error = null;
		});

		builder.addCase(banUser.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to ban user";
		});

		// Unban user
		builder.addCase(unbanUser.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(unbanUser.fulfilled, (state) => {
			state.isLoading = false;
			state.error = null;
		});

		builder.addCase(unbanUser.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to unban user";
		});

		// Delete user
		builder.addCase(deleteUser.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(deleteUser.fulfilled, (state) => {
			state.isLoading = false;
			state.error = null;
		});

		builder.addCase(deleteUser.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to delete user";
		});

		// Restore user
		builder.addCase(restoreUser.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(restoreUser.fulfilled, (state) => {
			state.isLoading = false;
			state.error = null;
		});

		builder.addCase(restoreUser.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to restore user";
		});

		// Get user by ID (admin)
		builder.addCase(getUserByIdAsAdmin.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			getUserByIdAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = null;
				// Store in userMap for quick access
				if (action.payload?.user) {
					const userId = action.payload.user._id || action.payload.user.userId;
					if (userId) {
						state.userMap[userId] = action.payload.user;
					}
				}
			},
		);

		builder.addCase(getUserByIdAsAdmin.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to fetch user";
		});

		// Update user by ID (admin) - does NOT update logged-in user's data
		builder.addCase(updateUserByIdAsAdmin.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			updateUserByIdAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = null;
				// Update in userMap if present
				if (action.payload?.user) {
					const userId = action.payload.user._id || action.payload.user.userId;
					if (userId && state.userMap[userId]) {
						state.userMap[userId] = action.payload.user;
					}
				}
				// Do NOT update userData - this is for editing other users, not the logged-in user
			},
		);

		builder.addCase(updateUserByIdAsAdmin.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to update user";
		});

		// Delete user by ID (admin)
		builder.addCase(deleteUserByIdAsAdmin.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(
			deleteUserByIdAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = null;
				// Remove from userMap if present
				if (action.payload?.userId) {
					delete state.userMap[action.payload.userId];
				}
			},
		);

		builder.addCase(deleteUserByIdAsAdmin.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error = action.payload || "Failed to delete user";
		});

		// ==================== Auto-Draft Admin Actions ====================

		// Get all auto-draft users
		builder.addCase(getAutoDraftUsersAsAdmin.pending, (state) => {
			state.autoDraftLoading = true;
			state.autoDraftError = null;
		});

		builder.addCase(
			getAutoDraftUsersAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftLoading = false;
				state.autoDraftError = null;
				state.autoDraftUsers = action.payload?.data || action.payload || [];
			},
		);

		builder.addCase(getAutoDraftUsersAsAdmin.rejected, (state, action: any) => {
			state.autoDraftLoading = false;
			state.autoDraftError =
				action.payload || "Failed to fetch auto-draft users";
		});

		// Get auto-draft user by ID
		builder.addCase(getAutoDraftUserByIdAsAdmin.pending, (state) => {
			state.autoDraftLoading = true;
			state.autoDraftError = null;
		});

		builder.addCase(
			getAutoDraftUserByIdAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftLoading = false;
				state.autoDraftError = null;
				state.autoDraftUserDetail = action.payload?.data || action.payload;
			},
		);

		builder.addCase(
			getAutoDraftUserByIdAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftLoading = false;
				state.autoDraftError =
					action.payload || "Failed to fetch auto-draft user details";
			},
		);

		// Get auto-draft summary
		builder.addCase(getAutoDraftSummaryAsAdmin.pending, (state) => {
			state.autoDraftLoading = true;
			state.autoDraftError = null;
		});

		builder.addCase(
			getAutoDraftSummaryAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftLoading = false;
				state.autoDraftError = null;
				state.autoDraftSummary = action.payload?.data || action.payload;
			},
		);

		builder.addCase(
			getAutoDraftSummaryAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftLoading = false;
				state.autoDraftError =
					action.payload || "Failed to fetch auto-draft summary";
			},
		);

		// Cancel auto-draft for user
		builder.addCase(cancelAutoDraftAsAdmin.pending, (state) => {
			state.autoDraftLoading = true;
			state.autoDraftError = null;
		});

		builder.addCase(
			cancelAutoDraftAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftLoading = false;
				state.autoDraftError = null;
				// Update the user in autoDraftUsers list if present
				const cancelledUserId = action.payload?.data?.userId;
				if (cancelledUserId && state.autoDraftUsers) {
					state.autoDraftUsers = state.autoDraftUsers.filter(
						(user) => user.userId !== cancelledUserId,
					);
				}
				// Clear autoDraftUserDetail if it's the cancelled user
				if (
					cancelledUserId &&
					state.autoDraftUserDetail?.userId === cancelledUserId
				) {
					state.autoDraftUserDetail = null;
				}
			},
		);

		builder.addCase(cancelAutoDraftAsAdmin.rejected, (state, action: any) => {
			state.autoDraftLoading = false;
			state.autoDraftError = action.payload || "Failed to cancel auto-draft";
		});

		// ==================== Auto-Draft Results Actions ====================

		// Get today's auto-draft results
		builder.addCase(getAutoDraftResultsAsAdmin.pending, (state) => {
			state.autoDraftResultsLoading = true;
			state.autoDraftResultsError = null;
		});

		builder.addCase(
			getAutoDraftResultsAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError = null;
				state.autoDraftResults = action.payload;
			},
		);

		builder.addCase(
			getAutoDraftResultsAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError =
					action.payload || "Failed to fetch auto-draft results";
			},
		);

		// Get auto-draft results by date
		builder.addCase(getAutoDraftResultsByDateAsAdmin.pending, (state) => {
			state.autoDraftResultsLoading = true;
			state.autoDraftResultsError = null;
		});

		builder.addCase(
			getAutoDraftResultsByDateAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError = null;
				state.autoDraftResults = action.payload;
			},
		);

		builder.addCase(
			getAutoDraftResultsByDateAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError =
					action.payload || "Failed to fetch auto-draft results for date";
			},
		);

		// Get failed auto-draft attempts
		builder.addCase(getAutoDraftFailedAsAdmin.pending, (state) => {
			state.autoDraftResultsLoading = true;
			state.autoDraftResultsError = null;
		});

		builder.addCase(
			getAutoDraftFailedAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError = null;
				state.autoDraftFailed = action.payload;
			},
		);

		builder.addCase(
			getAutoDraftFailedAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError =
					action.payload || "Failed to fetch failed auto-draft attempts";
			},
		);

		// Get auto-draft retry attempts
		builder.addCase(getAutoDraftRetriesAsAdmin.pending, (state) => {
			state.autoDraftResultsLoading = true;
			state.autoDraftResultsError = null;
		});

		builder.addCase(
			getAutoDraftRetriesAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError = null;
				state.autoDraftRetries = action.payload;
			},
		);

		builder.addCase(
			getAutoDraftRetriesAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError =
					action.payload || "Failed to fetch auto-draft retry attempts";
			},
		);

		// Get auto-draft attempt by ID
		builder.addCase(getAutoDraftAttemptByIdAsAdmin.pending, (state) => {
			state.autoDraftResultsLoading = true;
			state.autoDraftResultsError = null;
		});

		builder.addCase(
			getAutoDraftAttemptByIdAsAdmin.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError = null;
				state.autoDraftAttemptDetail = action.payload;
			},
		);

		builder.addCase(
			getAutoDraftAttemptByIdAsAdmin.rejected,
			(state, action: any) => {
				state.autoDraftResultsLoading = false;
				state.autoDraftResultsError =
					action.payload || "Failed to fetch auto-draft attempt details";
			},
		);
	},
});

export const {
	setAuthenticationState,
	updateUserProfileImage,
	setUserAccessState,
} = userSlice.actions;

export const { setAccountVerificationData } = accountVerificationSlice.actions;

export default userSlice.reducer;
